import { useMemo } from "react";
import { useGetContent, getGetContentQueryKey } from "@workspace/api-client-react";

export type ContentFieldType = "text" | "textarea" | "list";

export interface ContentField {
  key: string;
  label: string;
  type: ContentFieldType;
  /** Default for text / textarea fields. */
  default?: string;
  /** Default for list fields. */
  defaultList?: string[];
}

export interface ContentPageGroup {
  id: string;
  label: string;
  fields: ContentField[];
}

/**
 * The catalog of every editable piece of copy on the public site, grouped by
 * page. Defaults mirror the original hardcoded wording so the site always
 * renders even before anything is edited.
 */
export const CONTENT_PAGES: ContentPageGroup[] = [
  {
    id: "home",
    label: "Home",
    fields: [
      { key: "home.hero.tagline", label: "Hero tagline", type: "text", default: "LIGHT IT UP" },
      { key: "home.hero.subtitle", label: "Hero subtitle", type: "text", default: "You were always the show. We just turned on the lights." },
      { key: "home.hero.ctaPrimary", label: "Hero primary button", type: "text", default: "View Events" },
      { key: "home.hero.ctaSecondary", label: "Hero secondary button", type: "text", default: "Our Story" },
      {
        key: "home.marquee.phrases",
        label: "Scrolling banner phrases",
        type: "list",
        defaultList: ["LIGHT IT UP", "LUMA ENTERTAINMENT", "BOLD", "MAGNETIC", "ALIVE", "WOMEN FORWARD"],
      },
      { key: "home.events.heading", label: "Events section heading", type: "text", default: "EVENTS" },
      { key: "home.events.eyebrow", label: "Events section eyebrow", type: "text", default: "PRESENTED BY LUMA" },
      { key: "home.events.seeAll", label: "Events 'see all' link", type: "text", default: "See All" },
      { key: "home.events.cardCta", label: "Event card call-to-action", type: "text", default: "Watch Now" },
      { key: "home.events.empty", label: "Events empty message", type: "text", default: "No events scheduled yet. Stay tuned." },
      { key: "home.brand.eyebrow", label: "Brand section eyebrow", type: "text", default: "MYLUMA.TV" },
      { key: "home.brand.headingLine1", label: "Brand heading line 1", type: "text", default: "You were always" },
      { key: "home.brand.headingLine2", label: "Brand heading line 2", type: "text", default: "the show." },
      { key: "home.brand.subtitle", label: "Brand subtitle", type: "text", default: "We just turned on the lights." },
      { key: "home.brand.cta", label: "Brand button", type: "text", default: "Our Story" },
    ],
  },
  {
    id: "about",
    label: "About",
    fields: [
      { key: "about.hero.eyebrow", label: "Hero eyebrow", type: "text", default: "EST. MYLUMA.TV" },
      { key: "about.hero.headingLine1", label: "Hero heading line 1", type: "text", default: "The LUMA" },
      { key: "about.hero.headingLine2", label: "Hero heading line 2", type: "text", default: "Story" },
      { key: "about.hero.quote", label: "Hero quote", type: "text", default: "\"You were always the show. We just turned on the lights.\"" },
      { key: "about.body.paragraph1", label: "Body paragraph 1", type: "textarea", default: "LUMA Entertainment was born from a simple truth: the most magnetic personalities shouldn't have to navigate the darkness alone. We exist to amplify, to elevate, and to illuminate the talent that defines the modern era of entertainment." },
      { key: "about.body.paragraph2", label: "Body paragraph 2", type: "textarea", default: "LUMA means light in Latin. We are a premium entertainment brand — bold, magnetic, and alive. Women-forward without walls. We believe in raw authenticity, undeniable charisma, and the power of a perfectly orchestrated spectacle." },
      { key: "about.capabilities.eyebrow", label: "Capabilities eyebrow", type: "text", default: "CAPABILITIES" },
      { key: "about.capabilities.heading", label: "Capabilities heading", type: "text", default: "What We Do" },
      { key: "about.pillar1.title", label: "Pillar 1 title", type: "text", default: "Create & Film" },
      { key: "about.pillar1.body", label: "Pillar 1 body", type: "textarea", default: "We develop, shoot, and produce original entertainment from concept to final cut — cinematic content built to capture attention and own the feed." },
      { key: "about.pillar2.title", label: "Pillar 2 title", type: "text", default: "Host & Promote" },
      { key: "about.pillar2.body", label: "Pillar 2 body", type: "textarea", default: "We host the moments and run the machine behind them — booking, hosting, and the marketing engine that drives turnout, hype, and reach." },
      { key: "about.pillar3.title", label: "Pillar 3 title", type: "text", default: "In-Person Experiences" },
      { key: "about.pillar3.body", label: "Pillar 3 body", type: "textarea", default: "We produce and host live events that leave a mark — immersive, high-energy spectacles designed to be felt in the room and talked about everywhere else." },
      { key: "about.pillar4.title", label: "Pillar 4 title", type: "text", default: "Brand Partnerships" },
      { key: "about.pillar4.body", label: "Pillar 4 body", type: "textarea", default: "We connect talent and culture with the right brands — building collabs that feel authentic, move product, and expand reach on both sides." },
      { key: "about.pillar5.title", label: "Pillar 5 title", type: "text", default: "Cast & Discover" },
      { key: "about.pillar5.body", label: "Pillar 5 body", type: "textarea", default: "We find the magnetic personalities before the world does. Our eye for talent is sharp, our pipeline is deep, and we elevate careers from day one." },
      { key: "about.pillar6.title", label: "Pillar 6 title", type: "text", default: "Community & Culture" },
      { key: "about.pillar6.body", label: "Pillar 6 body", type: "textarea", default: "Great entertainment doesn't just entertain — it builds. We cultivate audiences, ignite fan connections, and shape the cultural moments people live inside." },
      { key: "about.pillar7.title", label: "Pillar 7 title", type: "text", default: "Light It Up" },
      { key: "about.pillar7.body", label: "Pillar 7 body", type: "textarea", default: "Our promise and our signature. Whatever we touch, we turn on the lights — taking talent, brands, and moments and making them impossible to look away from." },
      { key: "about.closing.heading", label: "Closing heading", type: "text", default: "Bold. Magnetic. Alive." },
      { key: "about.closing.subtitle", label: "Closing subtitle", type: "text", default: "We are the light you were always meant to step into." },
    ],
  },
  {
    id: "events",
    label: "Events",
    fields: [
      { key: "events.hero.eyebrow", label: "Hero eyebrow", type: "text", default: "LUMA PRESENTS" },
      { key: "events.hero.heading", label: "Hero heading", type: "text", default: "Events" },
      { key: "events.hero.subtitle", label: "Hero subtitle", type: "text", default: "The spectacles that define the culture." },
      { key: "events.intro.paragraph", label: "Intro paragraph", type: "textarea", default: "From sold-out productions to the moments everyone's talking about, LUMA events are built to be felt in the room and remembered long after the lights go down. This is entertainment turned all the way up." },
      { key: "events.empty", label: "Empty message", type: "text", default: "No events scheduled at this time. Stay tuned." },
      { key: "events.card.learnMore", label: "Event link fallback label", type: "text", default: "Learn More" },
      { key: "events.card.viewEvent", label: "View event page link label", type: "text", default: "Explore The Event" },
      { key: "events.card.participants", label: "Participants label", type: "text", default: "Participants" },
      { key: "events.card.noImage", label: "Missing image label", type: "text", default: "No image" },
      { key: "events.card.videoUnavailable", label: "Unavailable video label", type: "text", default: "Video unavailable" },
    ],
  },
  {
    id: "partnerships",
    label: "Partnerships",
    fields: [
      { key: "partnerships.hero.eyebrow", label: "Hero eyebrow", type: "text", default: "BRANDS WE BUILD WITH" },
      { key: "partnerships.hero.heading", label: "Hero heading", type: "text", default: "Partners" },
      { key: "partnerships.hero.quote", label: "Hero quote", type: "text", default: "\"The right collab doesn't interrupt the show — it becomes part of it.\"" },
      { key: "partnerships.intro.paragraph1", label: "Intro paragraph 1", type: "textarea", default: "LUMA connects magnetic talent with brands that get it. We don't do bolt-on sponsorships — we build partnerships that feel native to the moment, move product, and expand reach on both sides." },
      { key: "partnerships.intro.paragraph2", label: "Intro paragraph 2", type: "textarea", default: "From swimwear to spirits, beauty to tech, our partners step into the light alongside the personalities defining modern entertainment." },
      { key: "partnerships.roster.eyebrow", label: "Roster eyebrow", type: "text", default: "CURRENT PARTNERS" },
      { key: "partnerships.roster.heading", label: "Roster heading", type: "text", default: "In The Light" },
      { key: "partnerships.roster.empty", label: "Roster empty message", type: "text", default: "New partners stepping into the light soon." },
      { key: "partnerships.roster.affiliatesLabel", label: "Affiliates label (on partner cards)", type: "text", default: "Our Affiliates" },
      { key: "partnerships.cta.heading", label: "CTA heading", type: "text", default: "Want To Partner?" },
      { key: "partnerships.cta.subtitle", label: "CTA subtitle", type: "text", default: "Let's build something the culture can't ignore." },
      { key: "partnerships.cta.button", label: "CTA button", type: "text", default: "Work With Us" },
    ],
  },
  {
    id: "contact",
    label: "Contact",
    fields: [
      { key: "contact.hero.eyebrow", label: "Hero eyebrow", type: "text", default: "REACH OUT" },
      { key: "contact.hero.heading", label: "Hero heading", type: "text", default: "Get In Touch" },
      { key: "contact.hero.subtitle", label: "Hero subtitle", type: "text", default: "Business inquiries, press, and partnerships." },
      { key: "contact.info.emailLabel", label: "Email label", type: "text", default: "Email" },
      { key: "contact.info.locationLabel", label: "Location label", type: "text", default: "Location" },
      { key: "contact.quote", label: "Pull quote", type: "text", default: "Ready to step into the light?" },
      { key: "contact.form.nameLabel", label: "Form — name label", type: "text", default: "Name" },
      { key: "contact.form.emailLabel", label: "Form — email label", type: "text", default: "Email" },
      { key: "contact.form.messageLabel", label: "Form — message label", type: "text", default: "Message" },
      { key: "contact.form.submit", label: "Form — submit button", type: "text", default: "Send Message" },
    ],
  },
];

const FIELD_BY_KEY = new Map<string, ContentField>();
for (const page of CONTENT_PAGES) {
  for (const field of page.fields) FIELD_BY_KEY.set(field.key, field);
}

export function getFieldDefaultText(key: string): string {
  return FIELD_BY_KEY.get(key)?.default ?? "";
}

export function getFieldDefaultList(key: string): string[] {
  return FIELD_BY_KEY.get(key)?.defaultList ?? [];
}

export interface SiteContent {
  /** Resolve a text/textarea block, falling back to the catalog default. */
  text: (key: string) => string;
  /** Resolve a list block, falling back to the catalog default. */
  list: (key: string) => string[];
  /** Raw override map (key → stored value), for the admin editor. */
  overrides: Map<string, string>;
  isLoading: boolean;
}

export function useSiteContent(): SiteContent {
  const { data, isLoading } = useGetContent({
    query: {
      queryKey: getGetContentQueryKey(),
      // Site copy changes rarely, so we treat the cached content as fresh and do
      // NOT refetch on every mount. Edits propagate to public pages via the
      // explicit cache invalidation performed after an admin save (see
      // `handleSavePage` in AdminDashboard). This deliberately makes that
      // invalidation load-bearing: if it were dropped, public pages would keep
      // showing stale copy until a hard reload — a regression the e2e suite
      // ("saved copy renders on public pages via in-app navigation") guards.
      staleTime: 5 * 60 * 1000,
    },
  });

  const overrides = useMemo(() => {
    const map = new Map<string, string>();
    for (const block of data?.blocks ?? []) map.set(block.key, block.value);
    return map;
  }, [data]);

  return useMemo<SiteContent>(() => {
    const text = (key: string): string => {
      const v = overrides.get(key);
      if (v !== undefined && v.trim() !== "") return v;
      return getFieldDefaultText(key);
    };
    const list = (key: string): string[] => {
      const v = overrides.get(key);
      if (v !== undefined && v.trim() !== "") {
        try {
          const parsed = JSON.parse(v);
          if (Array.isArray(parsed)) {
            const items = parsed.filter((s): s is string => typeof s === "string" && s.trim() !== "");
            if (items.length > 0) return items;
          }
        } catch {
          // fall through to default
        }
      }
      return getFieldDefaultList(key);
    };
    return { text, list, overrides, isLoading };
  }, [overrides, isLoading]);
}
